import { useCallback, useEffect, useReducer, useState } from 'react';
import {
  automaticDelayMs,
  governedCaseReducer,
  initialGovernedCasePhase,
  resolveGovernedCaseTimings,
  type GovernedCasePhase,
  type GovernedCaseTimings,
} from './governed-case-state';

export interface GovernedCaseSequenceOptions {
  reducedMotion: boolean;
  saveData: boolean;
  timings?: Partial<GovernedCaseTimings>;
  /**
   * Presentation-only dwell before dispatching a simulated release event.
   * Omit when the caller needs an explicit practitioner-controlled threshold.
   */
  autoReleaseMs?: number | null;
}

export interface GovernedCaseSequence {
  phase: GovernedCasePhase;
  release: () => void;
}

/**
 * Drives the governed-case phase sequence with one finite timeout per
 * automatic phase. By default, `held` schedules nothing, so the threshold
 * holds indefinitely until the parent page calls `release()`. A presentation
 * can opt into `autoReleaseMs` to demonstrate the complete flow after a brief
 * review dwell.
 * Static preferences and auto-release configuration are captured once at
 * mount, so later preference or option changes never rewind the sequence.
 */
export function useGovernedCaseSequence(
  options: GovernedCaseSequenceOptions,
): GovernedCaseSequence {
  const [staticHold] = useState(options.reducedMotion || options.saveData);
  const [autoReleaseMs] = useState(() =>
    sanitizeAutoReleaseMs(options.autoReleaseMs),
  );
  const [phase, dispatch] = useReducer(
    governedCaseReducer,
    staticHold && autoReleaseMs !== null
      ? 'released'
      : initialGovernedCasePhase(staticHold),
  );

  const { alignMs, railMs, holdMs } = resolveGovernedCaseTimings(
    options.timings,
  );

  useEffect(() => {
    if (staticHold) return undefined;
    if (phase === 'held' && autoReleaseMs !== null) {
      const timer = window.setTimeout(
        () => dispatch({ type: 'RELEASE' }),
        autoReleaseMs,
      );
      return () => window.clearTimeout(timer);
    }

    const delay = automaticDelayMs(phase, { alignMs, railMs, holdMs });
    if (delay === null) return undefined;

    const timer = window.setTimeout(
      () => dispatch({ type: 'AUTO_ADVANCE' }),
      delay,
    );
    return () => window.clearTimeout(timer);
  }, [phase, staticHold, autoReleaseMs, alignMs, railMs, holdMs]);

  const release = useCallback(() => dispatch({ type: 'RELEASE' }), []);

  return { phase, release };
}

function sanitizeAutoReleaseMs(
  value: number | null | undefined,
): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}
